import React, { useEffect, useState } from 'react';
import './StreakReminderModal.css';

const COMMIT_PERIODS = [
  { id: '7d', label: '7D', tag: 'Kickoff' },
  { id: '30d', label: '30D', tag: 'Habit' },
  { id: '90d', label: '90D', tag: 'Legend' },
];

export default function StreakReminderModal({ isOpen, onClose, onTurnOnReminders }) {
  const [commitPeriod, setCommitPeriod] = useState('30d');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = () => onClose();

  const handleCta = async () => {
    if (!onTurnOnReminders) {
      handleClose();
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onTurnOnReminders();
      if (result?.ok) {
        setShowSuccess(true);
      }
    } catch {
      // Stay on form if parent handler rejects.
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const titleId = showSuccess
    ? 'streak-reminder-modal-success-title'
    : 'streak-reminder-modal-title';

  return (
    <div
      className="streak-reminder-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="streak-reminder-modal-backdrop"
        aria-label="Dismiss"
        onClick={handleClose}
      />
      <div className="streak-reminder-modal-dialog">
        <button
          type="button"
          className="streak-reminder-modal-close"
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="M18 6L6 18M6 6l12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {showSuccess ? (
          <div className="streak-reminder-modal-success">
            <div className="streak-reminder-modal-success-icon-wrap" aria-hidden="true">
              <div className="streak-reminder-modal-success-icon">
                <svg viewBox="0 0 24 24" width="36" height="36" fill="none">
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="#ffffff"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            <h2 id={titleId} className="streak-reminder-modal-success-title">
              Success. Let&apos;s grow your channel!
            </h2>
            <p className="streak-reminder-modal-success-sub">
              We&apos;ll notify you so you don&apos;t lose audience!
            </p>

            <button type="button" className="streak-reminder-modal-cta" onClick={handleClose}>
              <span className="streak-reminder-modal-cta-label">Got it</span>
            </button>
          </div>
        ) : (
          <>
            <div className="streak-reminder-modal-hero">
              <span className="streak-reminder-modal-sparkle streak-reminder-modal-sparkle--1" aria-hidden="true">✦</span>
              <span className="streak-reminder-modal-sparkle streak-reminder-modal-sparkle--2" aria-hidden="true">✦</span>
              <span className="streak-reminder-modal-sparkle streak-reminder-modal-sparkle--3" aria-hidden="true">✦</span>
              <div className="streak-reminder-modal-flame-badge">
                <img
                  src={`${process.env.PUBLIC_URL}/flame_badge.png`}
                  alt=""
                  className="streak-reminder-modal-flame-img"
                />
              </div>
            </div>

            <h2 id={titleId} className="streak-reminder-modal-title">
              Keep your
              <br />
              <span className="streak-reminder-modal-title-accent">Creator Streak Alive!</span>
            </h2>

            <p className="streak-reminder-modal-sub">
              Makereels can remind you when it&apos;s time to create and post, so your audience stays
              warm and your growth stays consistent.
            </p>

            <div className="streak-reminder-modal-stat-block">
              <div className="streak-reminder-modal-stat">
                <div className="streak-reminder-modal-stat-top">
                  <span className="streak-reminder-modal-stat-value">270X</span>
                  <span className="streak-reminder-modal-stat-hero-label">better reach</span>
                  <img
                    src={`${process.env.PUBLIC_URL}/stat_graph.png`}
                    alt=""
                    className="streak-reminder-modal-stat-graph"
                  />
                </div>
                <p className="streak-reminder-modal-stat-text">
                  Creators who post consistently often see dramatically better reach.
                </p>
              </div>
              <p className="streak-reminder-modal-stat-source">
                Source: Buffer Instagram posting frequency study 2025
              </p>
              <div className="streak-reminder-modal-commit">
                <h3 className="streak-reminder-modal-commit-heading">Choose your streak goal</h3>
                <div className="streak-reminder-modal-commit-row" role="group" aria-label="Choose your streak goal">
                  {COMMIT_PERIODS.map(({ id, label, tag }) => (
                    <button
                      key={id}
                      type="button"
                      className={`streak-reminder-modal-commit-btn${
                        commitPeriod === id ? ' streak-reminder-modal-commit-btn--active' : ''
                      }`}
                      aria-pressed={commitPeriod === id}
                      onClick={() => setCommitPeriod(id)}
                      disabled={isSubmitting}
                    >
                      <span className="streak-reminder-modal-commit-btn-label">{label}</span>
                      {commitPeriod === id ? (
                        <span className="streak-reminder-modal-commit-ribbon" aria-hidden="true">
                          {tag}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="streak-reminder-modal-cta"
              onClick={handleCta}
              disabled={isSubmitting}
            >
              <svg className="streak-reminder-modal-cta-bell" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.73 21a2 2 0 0 1-3.46 0"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="streak-reminder-modal-cta-label">
                {isSubmitting ? 'Turning on…' : 'Turn on posting reminders'}
              </span>
              {!isSubmitting ? (
                <svg className="streak-reminder-modal-cta-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : null}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

